<template>
  <NrModal
    :show="modalActive"
    primaryButtonText="Run report"
    secondaryButtonText="Cancel"
    title="Run request report"
    :disableButtons="noSelectedSavedReport || noSelectedNewReportOption"
    :hasSecondaryButton="true"
    @on-primary-click="submitReportForm"
    @on-secondary-click="$emit('on-close-modal')"
    @on-close-click="$emit('on-close-modal')"
  >
    <form @submit.prevent>
      <NrTabs v-model="activeTabId"
        ><NrTab title="New reports" id="new-reports-tab">
          <NrSelect
            v-model="selectedBasicInfo"
            :options="basicInfoOptions"
            multiple
          >
            <template v-slot:label>
              <span>Basic information</span>
            </template>
          </NrSelect>
          <NrSelect
            v-model="selectedReportDetails"
            :options="reportDetailOptions"
            multiple
          >
            <template v-slot:label>
              <span>Report details</span>
            </template>
          </NrSelect>
          <NrSelect
            v-model="selectedRequesterDetails"
            :options="requesterDetailOptions"
            multiple
          >
            <template v-slot:label>
              <span>Requester details</span>
            </template>
          </NrSelect>
          <NrSelect v-model="selectedReportType" :options="reportTypeOptions">
            <template v-slot:label>
              <span>Report format</span>
            </template>
          </NrSelect>
          <div class="report-info">
            Reports may take several minutes to load depending on the quantity
            of data. You can only run one report at a time.
          </div>
          <div class="field report-name-checkbox">
            <NrFormInput
              type="checkbox"
              label="Name and save report"
              name="name_and_save_report"
              v-model="reportNameCheckbox"
            />
          </div>
          <NrFormInput
            v-if="reportNameCheckbox"
            label="Report name"
            name="report_name"
            v-model="reportName"
            maxlength="30"
          />
          <span
            v-if="reportNameCheckbox && reportNameErrorMsg"
            class="has-text-danger is-size-7"
            >Enter a report name or disable the checkbox to run the
            report.</span
          > </NrTab
        ><NrTab title="Saved reports" id="saved-reports-tab">
          <NrSelect
            v-model="selectedSavedReport"
            :options="savedReportOptions"
            placeholder="Select or delete a saved report"
            @onDelete="deleteReport"
            hasDelete
            appendToBody
          >
            <template v-slot:label>
              <span>Saved reports</span>
            </template>
          </NrSelect>
          <NrSelect
            v-model="selectedReportType"
            :options="reportTypeOptions"
            appendToBody
          >
            <template v-slot:label>
              <span>Report format</span>
            </template>
          </NrSelect>
        </NrTab></NrTabs
      >
    </form>
  </NrModal>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { mapState } from 'vuex'
import NrSelect from '@nextrequestco/components/src/components/NrSelect/nr-select.vue'
import NrModal from '@nextrequestco/components/src/components/NrModal/nr-modal.vue'
import NrTabs from '@nextrequestco/components/src/components/NrTabs/nr-tabs.vue'
import NrTab from '@nextrequestco/components/src/components/NrTabs/nr-tab.vue'
import NrFormInput from '@nextrequestco/components/src/components/NrFormInput/nr-form-input.vue'
import { parseFilterParams } from '../store/parse-filter-params'
import API from '../../../vue_shared/api' // eslint-disable-line import/no-useless-path-segments

const BASIC_INFO_OPTIONS = [
  { id: 'pretty_id', label: 'Request id' },
  { id: 'request_created_at', label: 'Created at' },
  { id: 'request_text', label: 'Request text' },
  { id: 'original_request_text', label: 'Original request text' },
  { id: 'due_date', label: 'Due date' },
  { id: 'point_of_contact', label: 'Point of contact' },
  { id: 'request_date', label: 'Request date' },
  { id: 'status', label: 'Status' },
  { id: 'url', label: 'URL' },
  { id: 'visibility', label: 'Visibility' }
]
const REQUESTER_DETAIL_OPTIONS = [
  { id: 'requester_name', label: 'Name' },
  { id: 'requester_email', label: 'Email' },
  { id: 'requester_phone', label: 'Phone' },
  { id: 'requester_address', label: 'Address' },
  { id: 'requester_city', label: 'City' },
  { id: 'requester_state', label: 'State' },
  { id: 'requester_zipcode', label: 'Zipcode' },
  { id: 'requester_company', label: 'Company' }
]
const REPORT_DETAIL_OPTIONS = [
  { id: 'closed_date', label: 'Closed date' },
  { id: 'closure_reasons', label: 'Closure reasons' },
  { id: 'departments', label: 'Departments' },
  { id: 'format_received', label: 'Format received' },
  { id: 'staff_time_in_hrs', label: 'Staff time (hrs:minutes)' },
  { id: 'staff_time_in_minutes', label: 'Staff time (minutes)' },
  { id: 'tags', label: 'Tags' },
  { id: 'publish_date', label: 'Embargo ends on date' },
  { id: 'staff_cost', label: 'Staff cost' },
  { id: 'date_first_contact', label: 'Date first contact' },
  { id: 'first_contact_event', label: 'First contact event' },
  {
    id: 'anticipated_fulfilled_at',
    label: 'Anticipated fulfillment date'
  },
  { id: 'expiration_date', label: 'Expiration date' }
]
const REPORT_TYPE_OPTIONS = [
  { label: 'Excel (xlsx)', id: 'xlsx' },
  { label: 'Csv', id: 'csv' }
]

export default {
  setup() {
    return { v$: useVuelidate() }
  },
  components: {
    NrSelect,
    NrModal,
    NrTabs,
    NrTab,
    NrFormInput
  },
  props: {
    isReportModalActive: {
      type: Boolean,
      default: false
    },
    fullScreen: {
      type: Boolean,
      default: false
    }
  },

  mounted() {
    this.loadSettings()
  },

  data() {
    return {
      activeTabId: 'new-reports-tab',
      reportNameCheckbox: false,
      selectedBasicInfo: BASIC_INFO_OPTIONS,
      selectedReportDetails: REPORT_DETAIL_OPTIONS,
      selectedRequesterDetails: REQUESTER_DETAIL_OPTIONS,
      selectedReportType: { label: 'Excel (xlsx)', id: 'xlsx' },
      selectedSavedReport: '',
      reportName: '',
      reportNameErrorMsg: false,
      selectAllBasicInfoCheckbox: true,
      selectAllReportDetailsCheckbox: true,
      selectAllRequesterDetailsCheckbox: true,
      savedReports: [],
      requestFieldOptions: []
    }
  },
  validations() {
    return {
      reportName: { required }
    }
  },
  watch: {
    reportNameCheckbox(val) {
      if (val === false) {
        this.reportName = ''
      }
    }
  },
  computed: {
    ...mapState('requests', ['filters']),
    reportNameFieldClass() {
      return this.reportNameErrorMsg ? 'is-danger' : ''
    },
    newReportTabActive() {
      return this.activeTabId === 'new-reports-tab'
    },
    savedReportTabActive() {
      return this.activeTabId === 'saved-reports-tab'
    },
    reportNameRequired() {
      return !this.v$.reportName.required
    },
    noSelectedSavedReport() {
      return (
        this.savedReportTabActive &&
        !(this.selectedSavedReport || this.selectedSavedReport.id)
      )
    },
    noSelectedNewReportOption() {
      return (
        this.newReportTabActive &&
        this.selectedBasicInfo.length === 0 &&
        this.selectedReportDetails.length === 0 &&
        this.selectedRequesterDetails.length === 0
      )
    },
    getSavedReportName() {
      const savedReport = this.savedReports.find(
        (rep) => rep.id === this.selectedSavedReport
      )
      if (!savedReport) {
        return ''
      }
      return savedReport.value
    },
    savedReportOptions() {
      return this.savedReports.map(({ id, value }) => ({ id, label: value }))
    },
    modalActive() {
      return this.isReportModalActive
    },
    basicInfoOptions() {
      return BASIC_INFO_OPTIONS
    },
    requesterDetailOptions() {
      return REQUESTER_DETAIL_OPTIONS
    },
    reportDetailOptions() {
      return [...REPORT_DETAIL_OPTIONS, ...this.requestFieldOptions]
    },
    reportTypeOptions() {
      return REPORT_TYPE_OPTIONS
    }
  },
  methods: {
    deleteReport(option) {
      API.deleteReport(option.id)
        .then((response) => {
          if (response.status === 204) {
            const reports = this.savedReports.filter((rep) => {
              return rep.id !== option.id
            })
            this.savedReports = reports
            this.$toasted.success('Successfully deleted a saved report')
            this.selectedSavedReport = ''
          }
        })
        .catch(() => {
          this.$toasted.error('An error occured while deleting a saved report.')
        })
    },
    loadSettings() {
      API.fetchSavedReports().then((response) => {
        const savedReports = response.data.csv_layouts
        if (savedReports.length) {
          this.savedReports = savedReports
        } else {
          this.savedReports = []
        }
        this.requestFieldOptions = response.data.request_fields.map(
          (field) => ({ id: ['request_field', field.id], label: field.name })
        )
        this.selectedReportDetails = [
          ...this.selectedReportDetails,
          ...this.requestFieldOptions
        ]
      })
    },

    newReportPayload() {
      return {
        filter: parseFilterParams(this.filters),
        layout_options: [
          this.selectedBasicInfo.map(({ id }) => id),
          this.selectedReportDetails.map(({ id }) => id),
          this.selectedRequesterDetails.map(({ id }) => id)
        ].flat(),
        report_name: this.reportName,
        report_type: this.selectedReportType.id
      }
    },
    savedReportPayload() {
      return {
        csv_layout_id: this.selectedSavedReport.id,
        report_type: this.selectedReportType.id,
        filter: parseFilterParams(this.filters)
      }
    },
    submitReportForm() {
      // Check if checkbox and name required for error msg
      if (this.reportNameCheckbox && this.reportNameRequired) {
        this.toggleReportNameErrorMsg(true)
        return
      }
      this.toggleReportNameErrorMsg(false)
      const payload = this.newReportTabActive
        ? this.newReportPayload()
        : this.savedReportPayload()
      this.$emit('on-submit', payload)
    },
    toggleReportNameErrorMsg(val) {
      this.reportNameErrorMsg = val
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@nextrequestco/components/src/styles/tokens/tokens.scss';
.report-dropdown {
  width: 100%;
  &:hover,
  &:active,
  &:focus {
    background-color: white !important;
    color: #0f1c70 !important;
  }
  & > span:first-of-type {
    width: 100%;
    text-align: left;
  }
  &-text {
    color: #1c1c41;
    vertical-align: top;
  }
}
.report-dropdown-placeholder {
  color: #6e6e87;
}
.custom-report-dropdown-item {
  padding: 0.5rem 0.25rem 0.5rem 1rem;
  &:hover {
    background-color: #ebebef;
    color: #1c1c41;
  }
  &.is-active {
    background-color: #0f1c70;
    color: #ebebef;
  }
}
.report-name-input-field {
  height: 1.5rem;
}
.report-name-checkbox {
  margin-top: $spacing-16;
  margin-bottom: 0.25rem;
}
.dropdown-item-with-checkbox {
  background-color: white !important;
}
.new-reports-section {
  margin-bottom: -1rem;
}
.b-tabs .tab-content {
  overflow: visible;
}
.nr-modal {
  main {
    padding: 0;
  }
}
.report-info {
  border-radius: $border-radius-default;
  background-color: $color-yellow-100;
  font-weight: $font-weight-semibold;
  padding: $spacing-4 $spacing-8;
  font-size: $font-size-2;
  margin-top: $spacing-16;
}
</style>
<style lang="scss">
.nr-modal {
  .v-select.input {
    min-height: 2.5em;
    height: auto;
    padding-top: calc(0.25em - 1px);
    padding-bottom: calc(0.25em - 1px);
  }
}
</style>
